/**
 * Pure opening-edit planning: resize an opening's width or reposition it along
 * its host wall, from the single-room view. No React, no Firestore.
 *
 * Mirrors wallStretch.js's "compute a plan, validate, let the UI confirm"
 * shape but is far simpler: a single opening moves/resizes within its fixed
 * wall span, so there is no propagation, no ceiling regen, no processStructure
 * bump. The cross-room safety concern (editing the invisible far side of a
 * shared wall) is handled by the §1.4 validity gate, which runs against
 * structure-wide siblings + T-junction walls.
 *
 * See claude-docs/2026-06-12-single-room-measurement-editing.md.
 */
import { getWallAxisXZ } from './wallGeometryUtils';
import { updateStructureArray, isDemoedOpening } from './openingSyncUtils';
import {
  isSpanClear,
  firstBlockingInterval,
  computePerpWallIntervals,
} from './openingWallObstructions';

// --- Tunable constants (flagged for review) --------------------------------
// JAMB: minimum wall material the opening must leave at each wall end. 0 lets
// an opening sit flush to a corner (geometrically valid — the wall span is
// fixed). Raise if a nonzero structural jamb is desired.
export const JAMB = 0;
// Minimum opening width. A door is ~30" (0.76 m); 0.3 m is a generous floor
// that still rejects degenerate slivers.
export const MIN_OPENING_WIDTH = 0.3;

const arrayKeyForKind = (kind) =>
  kind === 'door' ? 'doors' : kind === 'window' ? 'windows' : 'openings';

/**
 * Geometry of an opening on its host wall, in the wall's own along-axis frame.
 *
 * @returns {{
 *   dir: {x:number, z:number},
 *   wallStart: {x:number, z:number},
 *   wallEnd: {x:number, z:number},
 *   wallLength: number,
 *   proj: number,        // distance from wallStart to the opening center
 *   halfWidth: number,
 * } | null}
 */
// True when (dirX,dirZ) points in the canonical world orientation: +x, or +z
// when the line is vertical. Used to orient a wall axis room-independently —
// the structure wall is shared between rooms, but its stored start→end order
// (and a room's traversal of it) is not, so we pin a single orientation.
const isCanonicalOrientation = (dirX, dirZ) =>
  dirX > 1e-9 || (Math.abs(dirX) <= 1e-9 && dirZ >= 0);

export const getOpeningWallFrame = (opening, wall) => {
  const axis = getWallAxisXZ(wall);
  if (!axis || !axis.len) return null;
  // Orient the axis canonically so wallStart/wallEnd (and therefore proj and
  // the 'wallStart'/'wallEnd' side labels) mean the same physical ends no
  // matter which room — or which wall-endpoint order — produced `axis`.
  const flip = !isCanonicalOrientation(axis.dirX, axis.dirZ);
  const dir = flip
    ? { x: -axis.dirX, z: -axis.dirZ }
    : { x: axis.dirX, z: axis.dirZ };
  const wallStart = {
    x: axis.midX - (dir.x * axis.len) / 2,
    z: axis.midZ - (dir.z * axis.len) / 2,
  };
  const wallEnd = {
    x: axis.midX + (dir.x * axis.len) / 2,
    z: axis.midZ + (dir.z * axis.len) / 2,
  };
  const center = opening?.center || {};
  const proj =
    (center.x - wallStart.x) * dir.x + (center.z - wallStart.z) * dir.z;
  const halfWidth = (opening?.dimensions?.x || 0) / 2;
  return { dir, wallStart, wallEnd, wallLength: axis.len, proj, halfWidth };
};

/**
 * Canonical, room-independent side of a gap measurement — 'wallStart' or
 * 'wallEnd' — from the gap's own world endpoints. A gap runs along the wall
 * between the opening edge and a wall corner; the outward direction (corner −
 * edge) is along the wall, and the two gaps of an opening always point
 * opposite ways. Projecting that direction onto the canonically-oriented wall
 * axis gives a stable label that agrees in both rooms (it uses only world
 * coordinates, never a room's traversal order), and matches getOpeningWallFrame.
 *
 * @param {[number,number]} cornerXZ - world [x,z] of the wall-corner endpoint
 * @param {[number,number]} edgeXZ - world [x,z] of the opening-edge endpoint
 * @returns {'wallStart'|'wallEnd'|null}
 */
export const canonicalGapSide = (cornerXZ, edgeXZ) => {
  if (!cornerXZ || !edgeXZ) return null;
  const vx = cornerXZ[0] - edgeXZ[0];
  const vz = cornerXZ[1] - edgeXZ[1];
  if (Math.abs(vx) <= 1e-9 && Math.abs(vz) <= 1e-9) return null;
  // The corner sits past wallStart (proj < opening) iff its outward direction
  // is against the canonical axis.
  return isCanonicalOrientation(vx, vz) ? 'wallEnd' : 'wallStart';
};

// Run the §1.4 validity gate on a candidate span. `checkWidth` adds the
// too-narrow check (resize only). Pushes one errors entry per failure.
const validateSpan = (span, width, obstructions, { checkWidth }) => {
  const errors = [];
  const [s, e] = span;
  const {
    wallLength = 0,
    siblings = [],
    perpIntervals = [],
  } = obstructions || {};

  if (checkWidth && width < MIN_OPENING_WIDTH) {
    errors.push({ code: 'too-narrow' });
  }
  if (s < JAMB - 1e-9 || e > wallLength - JAMB + 1e-9) {
    errors.push({ code: 'overflows-wall' });
  }
  for (const sib of siblings) {
    if (!(e <= sib.startM || s >= sib.endM)) {
      errors.push({ code: 'overlaps-sibling', ref: { id: sib.id ?? null } });
      break;
    }
  }
  if (!isSpanClear(span, perpIntervals)) {
    const iv = firstBlockingInterval(span, perpIntervals);
    errors.push({
      code: 'overlaps-t-junction',
      ref: { wallId: iv?.wallId ?? null },
    });
  }
  return errors;
};

const makePlan = ({ opening, kind, isShared, after, errors }) => ({
  structureOpeningId: opening.structureOpeningId || opening.id,
  kind,
  isShared: !!isShared,
  before: { center: opening.center, dimensions: opening.dimensions },
  after,
  errors,
});

// Convert an along-wall position back to a world center, preserving the
// opening's y (height) — the inverse of getOpeningWallFrame's projection.
const projToCenter = (newProj, frame, opening) => ({
  x: frame.wallStart.x + newProj * frame.dir.x,
  y: opening.center?.y,
  z: frame.wallStart.z + newProj * frame.dir.z,
});

/**
 * Resize an opening's width. The anchor is chosen by which gap (if any) is
 * locked — a locked gap is a pinned edge the resize grows/shrinks away from:
 *   - start gap locked → start edge fixed, end edge moves (asymmetric).
 *   - end gap locked   → end edge fixed, start edge moves (asymmetric).
 *   - neither locked   → symmetric (center fixed), the default.
 *   - both gaps locked → width is determined by them, so a resize is
 *                        over-constrained → blocked.
 *
 * @param {Object} opening - structure opening ({ center, dimensions, ... })
 * @param {Object} wall - host structure wall
 * @param {Object} obstructions - { wallLength, siblings, perpIntervals }
 * @param {number} newWidthMeters
 * @param {{ kind?: string, isShared?: boolean, locks?: {startLocked,endLocked} }} meta
 * @returns {Object} OpeningEditPlan
 */
export const computeResize = (
  opening,
  wall,
  obstructions,
  newWidthMeters,
  { kind, isShared, locks } = {}
) => {
  const frame = getOpeningWallFrame(opening, wall);
  if (!frame) {
    return makePlan({
      opening,
      kind,
      isShared,
      after: { center: opening.center, dimensions: opening.dimensions },
      errors: [{ code: 'no-frame' }],
    });
  }
  const startLocked = !!locks?.startLocked;
  const endLocked = !!locks?.endLocked;
  // Both edges pinned ⟹ width is fixed; can't resize without unlocking one.
  if (startLocked && endLocked) {
    return makePlan({
      opening,
      kind,
      isShared,
      after: { center: opening.center, dimensions: opening.dimensions },
      errors: [{ code: 'over-constrained' }],
    });
  }

  const { proj } = frame;
  const oldWidth = frame.halfWidth * 2;
  // Anchor the fixed edge: shifting one edge moves the center by half the
  // width delta. Symmetric (no lock) keeps the center exactly put.
  let newProj;
  if (startLocked) newProj = proj + (newWidthMeters - oldWidth) / 2;
  else if (endLocked) newProj = proj - (newWidthMeters - oldWidth) / 2;
  else newProj = proj;

  const newHalf = newWidthMeters / 2;
  const span = [newProj - newHalf, newProj + newHalf];
  const after = {
    // Keep the exact original center for the symmetric case (no FP drift);
    // recompute only when an anchored edge moved it.
    center:
      startLocked || endLocked
        ? projToCenter(newProj, frame, opening)
        : opening.center,
    dimensions: { ...opening.dimensions, x: newWidthMeters },
  };
  const errors = validateSpan(span, newWidthMeters, obstructions, {
    checkWidth: true,
  });
  return makePlan({ opening, kind, isShared, after, errors });
};

/**
 * Edit the `anchorCorner` gap of an opening to `newGapMeters`. What happens to
 * the opening depends on which neighbour (the opposite gap / the width) is
 * locked — the locked edge stays put and the change is absorbed elsewhere:
 *
 *   - opposite gap locked → RESIZE: the opposite edge is pinned, so changing
 *     this gap changes the width (the edited edge moves, the far edge stays).
 *   - opposite gap unlocked → SLIDE: width is preserved, the whole opening
 *     moves; the opposite gap absorbs the change. (Default, matches "move the
 *     opening". A locked width also slides — width is preserved either way.)
 *   - opposite gap AND width both locked → over-constrained → blocked.
 *
 * Moves **relative to the opening's current position**: the gap delta is
 * `newGapMeters − currentGapMeters`, applied to the opening's actual center
 * (projected onto the structure wall). This cancels the room-frame vs
 * structure-frame origin difference (the clicked gap is measured against the
 * room wall; this validates against the structure wall), so a no-op never
 * moves the opening and never spuriously overflows. Bounds/sibling/T-junction
 * are still checked on the structure wall. When `currentGapMeters` is omitted
 * we fall back to the structure-frame gap.
 *
 * @param {Object} opening - structure opening
 * @param {Object} wall - host structure wall
 * @param {Object} obstructions - { wallLength, siblings, perpIntervals }
 * @param {'wallStart'|'wallEnd'} anchorCorner - corner the clicked gap measured from
 * @param {number} newGapMeters
 * @param {{ kind?, isShared?, currentGapMeters?, locks? }} meta
 * @returns {Object} OpeningEditPlan
 */
export const computeReposition = (
  opening,
  wall,
  obstructions,
  anchorCorner,
  newGapMeters,
  { kind, isShared, currentGapMeters, locks } = {}
) => {
  const frame = getOpeningWallFrame(opening, wall);
  if (!frame) {
    return makePlan({
      opening,
      kind,
      isShared,
      after: { center: opening.center, dimensions: opening.dimensions },
      errors: [{ code: 'no-frame' }],
    });
  }
  const { halfWidth, wallLength, proj } = frame;
  const oldWidth = halfWidth * 2;

  // The neighbour on the far side of the opening from the edited gap.
  const oppositeLocked =
    anchorCorner === 'wallStart' ? !!locks?.endLocked : !!locks?.startLocked;
  const widthLocked = !!locks?.widthLocked;
  // Far edge pinned AND width pinned ⟹ this gap is determined; can't edit it.
  if (oppositeLocked && widthLocked) {
    return makePlan({
      opening,
      kind,
      isShared,
      after: { center: opening.center, dimensions: opening.dimensions },
      errors: [{ code: 'over-constrained' }],
    });
  }

  // Structure-frame gap of the current position — baseline when the caller
  // doesn't pass the room-frame measured gap.
  const structureGap =
    anchorCorner === 'wallStart'
      ? proj - halfWidth
      : wallLength - proj - halfWidth;
  const baselineGap =
    typeof currentGapMeters === 'number' ? currentGapMeters : structureGap;
  const delta = newGapMeters - baselineGap; // +gap grows
  const sign = anchorCorner === 'wallStart' ? 1 : -1;

  let newProj;
  let newWidth;
  if (oppositeLocked) {
    // Resize: far edge fixed, edited edge moves by `delta` → width shrinks by
    // `delta`, center shifts by half that, toward the moving edge.
    newWidth = oldWidth - delta;
    newProj = proj + sign * (delta / 2);
  } else {
    // Slide: width preserved, the whole opening moves by `delta`.
    newWidth = oldWidth;
    newProj = proj + sign * delta;
  }

  const newHalf = newWidth / 2;
  const span = [newProj - newHalf, newProj + newHalf];
  const after = {
    center: projToCenter(newProj, frame, opening),
    dimensions: oppositeLocked
      ? { ...opening.dimensions, x: newWidth }
      : opening.dimensions,
  };
  // Only a resize changes the width, so only then is too-narrow possible.
  const errors = validateSpan(span, newWidth, obstructions, {
    checkWidth: oppositeLocked,
  });
  return makePlan({ opening, kind, isShared, after, errors });
};

/**
 * Edit the gap BETWEEN two adjacent openings (left, right) on the same wall to
 * `newGapMeters`. Which openings move/resize depends on whether each one's FAR
 * side (the side away from this gap) is pinned:
 *   - neither far pinned → symmetric move: left shifts −Δ/2, right +Δ/2 (widths fixed).
 *   - one far pinned     → the pinned opening stays, the other absorbs the full Δ.
 *   - both far pinned    → both stay; each width changes by −Δ/2 (gap grows → shrink).
 * (Δ = newGap − currentGap; the room-frame baseline cancels the frame offset as
 * in computeReposition.)
 *
 * Returns a MULTI-opening plan (`changes` array). Each moved/resized opening is
 * validated against the wall bounds, the OTHER openings on the wall (the pair is
 * excluded from each other's siblings), and T-junctions; the new gap can't go
 * negative and resized widths can't drop below MIN_OPENING_WIDTH.
 *
 * @param {{opening,kind}} leftRef - { opening: left structure opening, kind }
 * @param {{opening,kind}} rightRef - { opening: right structure opening, kind }
 * @param {Object} wall - host structure wall
 * @param {Object} obstructions - from buildPairObstructions
 * @param {number} newGapMeters
 * @param {{ isShared?, currentGapMeters?, leftFarLocked?, rightFarLocked? }} meta
 * @returns {Object} multi-opening OpeningEditPlan
 */
export const computeInterGapEdit = (
  leftRef,
  rightRef,
  wall,
  obstructions,
  newGapMeters,
  { isShared, currentGapMeters, leftFarLocked, rightFarLocked } = {}
) => {
  const left = leftRef.opening;
  const right = rightRef.opening;
  const fL = getOpeningWallFrame(left, wall);
  const fR = getOpeningWallFrame(right, wall);
  const mkMulti = (changes, errors) => ({
    changes,
    isShared: !!isShared,
    errors,
  });
  const noChange = [
    {
      structureOpeningId: left.structureOpeningId || left.id,
      kind: leftRef.kind,
      before: { center: left.center, dimensions: left.dimensions },
      after: { center: left.center, dimensions: left.dimensions },
    },
    {
      structureOpeningId: right.structureOpeningId || right.id,
      kind: rightRef.kind,
      before: { center: right.center, dimensions: right.dimensions },
      after: { center: right.center, dimensions: right.dimensions },
    },
  ];
  if (!fL || !fR) return mkMulti(noChange, [{ code: 'no-frame' }]);

  const structureGap = fR.proj - fR.halfWidth - (fL.proj + fL.halfWidth);
  const baseline =
    typeof currentGapMeters === 'number' ? currentGapMeters : structureGap;
  const delta = newGapMeters - baseline;

  let leftProj = fL.proj;
  let rightProj = fR.proj;
  let leftW = fL.halfWidth * 2;
  let rightW = fR.halfWidth * 2;
  let widthsChange = false;

  if (leftFarLocked && rightFarLocked) {
    // Both far edges pinned → adjust widths symmetrically, centers shift ±Δ/4.
    widthsChange = true;
    leftW -= delta / 2;
    rightW -= delta / 2;
    leftProj -= delta / 4;
    rightProj += delta / 4;
  } else if (leftFarLocked) {
    rightProj += delta; // left pinned, right absorbs
  } else if (rightFarLocked) {
    leftProj -= delta; // right pinned, left absorbs
  } else {
    leftProj -= delta / 2; // symmetric separation/approach
    rightProj += delta / 2;
  }

  const leftSpan = [leftProj - leftW / 2, leftProj + leftW / 2];
  const rightSpan = [rightProj - rightW / 2, rightProj + rightW / 2];
  const errors = [
    ...validateSpan(leftSpan, leftW, obstructions, {
      checkWidth: widthsChange,
    }),
    ...validateSpan(rightSpan, rightW, obstructions, {
      checkWidth: widthsChange,
    }),
  ];
  // The pair is excluded from each other's siblings, so guard their mutual
  // overlap explicitly: the new gap can't go negative.
  if (rightSpan[0] < leftSpan[1] - 1e-9)
    errors.push({ code: 'openings-collide' });

  const changes = [
    {
      structureOpeningId: left.structureOpeningId || left.id,
      kind: leftRef.kind,
      before: { center: left.center, dimensions: left.dimensions },
      after: {
        center: projToCenter(leftProj, fL, left),
        dimensions: { ...left.dimensions, x: leftW },
      },
    },
    {
      structureOpeningId: right.structureOpeningId || right.id,
      kind: rightRef.kind,
      before: { center: right.center, dimensions: right.dimensions },
      after: {
        center: projToCenter(rightProj, fR, right),
        dimensions: { ...right.dimensions, x: rightW },
      },
    },
  ];
  return mkMulti(changes, errors);
};

// --- Classification --------------------------------------------------------

// Kind of a 'pier' segment endpoint, given the selected room's measured
// (door/window) opening copy ids. `null` is the synthetic wall corner (the
// t=0 / t=1 items carry no refObjectId); a known opening copy id is an
// opening; anything else is a fixture-group member.
const endpointKind = (objId, roomOpeningIds) => {
  if (objId == null) return 'wall-corner';
  if (roomOpeningIds && roomOpeningIds.has(objId)) return 'opening';
  return 'fixture';
};

// Edit modes whose measurement can be edited in place from the current view
// (the opening editor). `cross-room` (wall length) is editable too, but only
// from the whole-structure view via stretch — callers handle that separately.
// `readonly` (fixture group/inner detail, unresolved openings) is not editable.
const EDITABLE_EDIT_MODES = new Set([
  'opening-resize',
  'opening-reposition',
  'opening-gap',
]);

export const isEditableEditMode = (mode) => EDITABLE_EDIT_MODES.has(mode);

/**
 * Route a clicked measurement to its edit mode. Pure; keyed on the real
 * `subtype` (+ the 'pier' endpoint kinds). Sharedness does not change the
 * route — it only sets `isShared` for the panel note + multi-room write.
 *
 * @param {Object} measurement - raw fixture-node measurement (has subtype, fromObjectId, toObjectId, wallId)
 * @param {Object} ctx
 * @param {Set<string>} ctx.roomOpeningIds - selected room's door/window copy ids
 * @param {Map<string,string>} ctx.structureOpeningIdByCopyId - copy id → structure opening id
 * @param {Map<string,Set<string>>} ctx.openingMembership - structure opening id → room ids
 * @returns {{ mode, openingId?, structureOpeningId?, anchor?, isShared? }}
 *          mode ∈ stretch|opening-resize|opening-reposition|cross-room|readonly|deferred
 */
export const classifyMeasurementEdit = (measurement, ctx = {}) => {
  const {
    roomOpeningIds = new Set(),
    structureOpeningIdByCopyId = new Map(),
    openingMembership = new Map(),
  } = ctx;
  const subtype = measurement?.subtype;

  const resolve = (copyId) => {
    const structureOpeningId = structureOpeningIdByCopyId.get(copyId) || copyId;
    const members = openingMembership.get(structureOpeningId);
    const isShared = !!members && members.size > 1;
    return { openingId: copyId, structureOpeningId, isShared };
  };

  if (subtype === 'wall-length' || subtype === 'wall-total') {
    return { mode: 'cross-room' };
  }

  if (subtype === 'opening-width') {
    const copyId = measurement.fromObjectId ?? measurement.toObjectId;
    if (copyId == null) return { mode: 'readonly' };
    return { mode: 'opening-resize', ...resolve(copyId) };
  }

  if (subtype === 'pier') {
    const fromKind = endpointKind(measurement.fromObjectId, roomOpeningIds);
    const toKind = endpointKind(measurement.toObjectId, roomOpeningIds);

    const isCornerOpening =
      (fromKind === 'wall-corner' && toKind === 'opening') ||
      (fromKind === 'opening' && toKind === 'wall-corner');
    if (isCornerOpening) {
      const cornerIsFrom = measurement.fromObjectId == null;
      const copyId = cornerIsFrom
        ? measurement.toObjectId
        : measurement.fromObjectId;
      // Side label, room-independent: prefer the gap's own world geometry
      // (canonicalGapSide), so a shared opening's two rooms agree on which
      // physical edge a per-side lock pins. Fall back to the t=0/t=1 null
      // endpoint when the measurement carries no world points (e.g. unit data).
      const corner = cornerIsFrom ? measurement.start : measurement.end;
      const edge = cornerIsFrom ? measurement.end : measurement.start;
      const anchor =
        canonicalGapSide(corner, edge) ||
        (cornerIsFrom ? 'wallStart' : 'wallEnd');
      return { mode: 'opening-reposition', anchor, ...resolve(copyId) };
    }
    if (fromKind === 'opening' && toKind === 'opening') {
      // Inter-opening gap: editable, moves/resizes BOTH openings (§ inter-gap).
      // Resolve which is left/right room-independently from world geometry so
      // the lock key + far-side logic agree across a shared wall's two rooms.
      const start = measurement.start;
      const end = measurement.end;
      const fromIsLeft =
        start && end
          ? isCanonicalOrientation(end[0] - start[0], end[1] - start[1])
          : true; // fallback: from = left (deterministic) when no world points
      const leftCopyId = fromIsLeft
        ? measurement.fromObjectId
        : measurement.toObjectId;
      const rightCopyId = fromIsLeft
        ? measurement.toObjectId
        : measurement.fromObjectId;
      const leftRes = resolve(leftCopyId);
      const rightRes = resolve(rightCopyId);
      return {
        mode: 'opening-gap',
        leftOpeningId: leftCopyId,
        rightOpeningId: rightCopyId,
        leftStructureId: leftRes.structureOpeningId,
        rightStructureId: rightRes.structureOpeningId,
        isShared: leftRes.isShared || rightRes.isShared,
      };
    }
    return { mode: 'readonly' };
  }

  return { mode: 'readonly' };
};

/**
 * Lock key for an editable opening measurement, derived from a classification.
 * Stamped onto opening measurements (canvas + panel) so they share the stretch
 * `measurementLocks` store, locked-keys set, and 🔒 glyph via the shared
 * `getMeasurementKey`.
 *
 * An opening has THREE independently-lockable measurements — width and the two
 * wall-end gaps — because they are related (startGap + width + endGap = wall
 * length), so a locked one pins an edge that other edits anchor on. Each side
 * is its own key (locking one gap does NOT lock the other):
 *   - width            → `opening-width:{id}`
 *   - wallStart gap    → `opening-gap-start:{id}`
 *   - wallEnd gap      → `opening-gap-end:{id}`
 *
 * @param {{ mode?: string, structureOpeningId?: string, anchor?: string }} classification
 * @returns {string|null}
 */
export const getOpeningMeasurementKey = (classification) => {
  if (classification?.mode === 'opening-gap') {
    return getInterGapMeasurementKey(classification);
  }
  const id = classification?.structureOpeningId;
  if (!id) return null;
  if (classification.mode === 'opening-resize') return `opening-width:${id}`;
  if (classification.mode === 'opening-reposition') {
    return classification.anchor === 'wallEnd'
      ? `opening-gap-end:${id}`
      : `opening-gap-start:${id}`;
  }
  return null;
};

/**
 * Lock key for an inter-opening gap measurement: keyed by both openings
 * (left/right already canonical, so the order is room-independent). Separate
 * from getOpeningMeasurementKey because the classification has no single
 * structureOpeningId.
 *
 * @param {{ leftStructureId?, rightStructureId? }} classification
 * @returns {string|null}
 */
export const getInterGapMeasurementKey = (classification) => {
  const l = classification?.leftStructureId;
  const r = classification?.rightStructureId;
  if (!l || !r) return null;
  return `opening-gap-between:${l}:${r}`;
};

/**
 * Read which of an opening's three measurements are currently locked, from the
 * structure's `measurementLocks` map. Locks are keyed by structure opening id,
 * so this is room-independent — a lock set from one room of a shared opening is
 * seen from the other.
 *
 * @param {Object} locks - structure measurementLocks map (key → value)
 * @param {string} structureOpeningId
 * @returns {{ startLocked: boolean, endLocked: boolean, widthLocked: boolean }}
 */
export const getOpeningLockState = (locks, structureOpeningId) => {
  const m = locks || {};
  const id = structureOpeningId;
  return {
    startLocked: `opening-gap-start:${id}` in m,
    endLocked: `opening-gap-end:${id}` in m,
    widthLocked: `opening-width:${id}` in m,
  };
};

/**
 * Normalize a plan to a flat list of per-opening changes. Single-opening plans
 * (resize/reposition) carry the change at top level; an inter-gap plan carries
 * a `changes` array (it moves/resizes two openings). One reader for both.
 *
 * @param {Object} plan - OpeningEditPlan
 * @returns {Array<{structureOpeningId, kind, after}>}
 */
export const planChanges = (plan) =>
  plan?.changes || [
    {
      structureOpeningId: plan.structureOpeningId,
      kind: plan.kind,
      after: plan.after,
    },
  ];

/**
 * Produce a preview structure with each changed opening's center/dimensions
 * swapped to its `after`. Reused by the canvas ghost and the final write so
 * preview and persistence can't drift (same pattern as applyStretchPlan).
 *
 * @param {Object} structureData - { doors, windows, openings, ... }
 * @param {Object} plan - OpeningEditPlan (single or multi-opening)
 * @returns {Object} new structureData (shallow clone with affected arrays replaced)
 */
export const applyOpeningEditToStructureData = (structureData, plan) => {
  if (!structureData || !plan) return structureData;
  let next = structureData;
  for (const change of planChanges(plan)) {
    const key = arrayKeyForKind(change.kind);
    const nextArray = updateStructureArray(
      next[key] || [],
      change.structureOpeningId,
      (entry) => ({
        ...entry,
        center: change.after.center,
        dimensions: change.after.dimensions,
      })
    );
    next = { ...next, [key]: nextArray };
  }
  return next;
};

/**
 * Gather the §1.4 obstruction bundle for an opening on its host wall. Siblings
 * come from STRUCTURE openings on the same parent wall (covers both rooms of a
 * shared wall); perpIntervals from structure walls T-juncting the host.
 *
 * @param {Object} structureOpening - the opening being edited
 * @param {Object} hostWall - its parentWallIds[0] structure wall
 * @param {Object} structureData - { walls, doors, windows, openings }
 * @returns {{ wallLength, siblings, perpIntervals }}
 */
// Sibling spans of OTHER structure openings on the host wall, excluding the
// opening(s) being edited and their co-located twins. A candidate that already
// overlaps a primary opening's current span is its own twin (a door and its
// frameless `opening` entry are the same physical doorway, recorded once in
// `doors` and once in `openings`) or pre-existing overlapping data — including
// it would spuriously block every edit, so it's dropped. EPS keeps a sibling
// that merely touches or barely overlaps (< 1 mm). `primaries` is the 1 (resize/
// reposition) or 2 (inter-gap) openings being edited.
const TWIN_EPS = 1e-3;
const gatherWallSiblings = (structureData, hostWall, primaries) => {
  const hostWallId = hostWall?.id;
  const excludeIds = new Set(primaries.map((o) => o.id));
  const primSpans = primaries
    .map((o) => getOpeningWallFrame(o, hostWall))
    .filter(Boolean)
    .map((f) => [f.proj - f.halfWidth, f.proj + f.halfWidth]);
  const isTwin = (startM, endM) =>
    primSpans.some(
      ([s, e]) => !(endM <= s + TWIN_EPS || startM >= e - TWIN_EPS)
    );
  const siblings = [];
  for (const key of ['doors', 'windows', 'openings']) {
    for (const o of structureData?.[key] || []) {
      if (!o || excludeIds.has(o.id)) continue;
      if (o.parentWallIds?.[0] !== hostWallId) continue;
      const f = getOpeningWallFrame(o, hostWall);
      if (!f) continue;
      const startM = f.proj - f.halfWidth;
      const endM = f.proj + f.halfWidth;
      if (isTwin(startM, endM)) continue;
      siblings.push({ id: o.id, startM, endM });
    }
  }
  return siblings;
};

/**
 * Gather the §1.4 obstruction bundle for an opening on its host wall. Siblings
 * come from STRUCTURE openings on the same parent wall (covers both rooms of a
 * shared wall); perpIntervals from structure walls T-juncting the host.
 *
 * @param {Object} structureOpening - the opening being edited
 * @param {Object} hostWall - its parentWallIds[0] structure wall
 * @param {Object} structureData - { walls, doors, windows, openings }
 * @returns {{ wallLength, siblings, perpIntervals }}
 */
export const buildObstructions = (
  structureOpening,
  hostWall,
  structureData
) => {
  const frame = getOpeningWallFrame(structureOpening, hostWall);
  return {
    wallLength: frame?.wallLength || 0,
    siblings: gatherWallSiblings(structureData, hostWall, [structureOpening]),
    perpIntervals: computePerpWallIntervals(hostWall, structureData?.walls),
  };
};

/**
 * Obstruction bundle for an inter-opening gap edit: the two edited openings are
 * excluded from each other's siblings (they're allowed to be adjacent; they're
 * validated against each other via the gap, not the sibling gate). Everything
 * else on the wall still blocks.
 *
 * @param {Object} left - left structure opening
 * @param {Object} right - right structure opening
 * @param {Object} hostWall
 * @param {Object} structureData
 * @returns {{ wallLength, siblings, perpIntervals }}
 */
export const buildPairObstructions = (left, right, hostWall, structureData) => {
  const frame = getOpeningWallFrame(left, hostWall);
  return {
    wallLength: frame?.wallLength || 0,
    siblings: gatherWallSiblings(structureData, hostWall, [left, right]),
    perpIntervals: computePerpWallIntervals(hostWall, structureData?.walls),
  };
};

const KIND_BY_ARRAY = { doors: 'door', windows: 'window', openings: 'opening' };

// An opening's vertical extent, or null when it carries no height to compare.
const verticalSpan = (o) => {
  const y = o?.center?.y;
  const h = o?.dimensions?.y;
  if (!Number.isFinite(y) || !Number.isFinite(h)) return null;
  return [y - h / 2, y + h / 2];
};

/**
 * The opening already on `hostWall` that a NEW one would be drawn over, or
 * null when its spot is free (#2837 — the door tool put a door straight on
 * top of a window). Two openings collide when their spans overlap ALONG the
 * wall and UP it, so a transom over a door, or a window beside one, is
 * fine; touching edges (< 1 mm) are fine too.
 *
 * Not `gatherWallSiblings`: that drops a sibling overlapping the edited
 * opening as its twin, which is exactly the case placement must catch.
 *
 * What never blocks: a wall-demolition marker (`category.opening.demoed` —
 * it spans whatever it removes) and an opening marked for demolition, which
 * a new opening may replace. That mark lives on the opening's ROOM copies,
 * so the caller passes their structure ids as `demoedIds`.
 *
 * @param {Object} newOpening - the structure-level element about to be added
 * @param {Object} hostWall - its parentWallIds[0] structure wall
 * @param {Object} structureData - { doors, windows, openings }
 * @param {{ demoedIds?: Set<string> }} [options]
 * @returns {{ id: string, kind: 'door'|'window'|'opening' } | null}
 */
export const findOpeningOverlapOnWall = (
  newOpening,
  hostWall,
  structureData,
  { demoedIds = new Set() } = {}
) => {
  const frame = getOpeningWallFrame(newOpening, hostWall);
  if (!frame) return null;
  const start = frame.proj - frame.halfWidth;
  const end = frame.proj + frame.halfWidth;
  const up = verticalSpan(newOpening);
  for (const [key, kind] of Object.entries(KIND_BY_ARRAY)) {
    for (const o of structureData?.[key] || []) {
      if (!o || o.id === newOpening.id) continue;
      if (o.parentWallIds?.[0] !== hostWall?.id) continue;
      if (isDemoedOpening(o) || o.demoed === true || demoedIds.has(o.id)) {
        continue;
      }
      const f = getOpeningWallFrame(o, hostWall);
      if (!f) continue;
      const oStart = f.proj - f.halfWidth;
      const oEnd = f.proj + f.halfWidth;
      if (end <= oStart + TWIN_EPS || start >= oEnd - TWIN_EPS) continue;
      const oUp = verticalSpan(o);
      if (
        up &&
        oUp &&
        (up[1] <= oUp[0] + TWIN_EPS || up[0] >= oUp[1] - TWIN_EPS)
      ) {
        continue;
      }
      return { id: o.id, kind };
    }
  }
  return null;
};
